import { useCallback, useEffect, useState } from 'react';
import { Loader2, ExternalLink, Briefcase, Pencil, ShieldAlert, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../ThemeProvider';
import { businessTokens, statusPill } from './businessTheme';
import { BusinessService } from '../../services/BusinessService';
import { openExternalUrl } from '../../utils/openExternal';
import { BusinessRegistration, BusinessProfile, BUSINESS_COOLDOWN_DAYS } from '../../types/business';

/**
 * The business review queue, rendered as a tab inside the Legion Remote's
 * action panel rather than as a new admin surface.
 *
 * Three queues in one, because they are the same job:
 *   • Applications      new pitches awaiting approve/deny
 *   • Name/link changes live profiles asking to change what was approved
 *   • Profiles          the suspend and restore controls, suspended first
 *
 * Every action here writes to moderation_logs through the same logAuditAction
 * the Watcher panel uses, so a business decision appears in the audit feed
 * alongside every other moderator action.
 */

type QueueTab = 'applications' | 'edits' | 'live';

export function BusinessReviewQueue({ adminId }: { adminId: string }) {
  const { theme } = useTheme();
  const t = businessTokens(theme);

  const [tab, setTab] = useState<QueueTab>('applications');
  const [registrations, setRegistrations] = useState<BusinessRegistration[]>([]);
  const [pendingEdits, setPendingEdits] = useState<BusinessProfile[]>([]);
  const [allProfiles, setAllProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [regs, edits, profiles] = await Promise.all([
      BusinessService.fetchRegistrations('pending'),
      BusinessService.fetchPendingEdits(),
      // Not the Explore deck: that filters to active, which would hide the
      // suspended profiles this tab exists to restore. Suspended ones sort
      // first.
      BusinessService.fetchAllProfilesForAdmin(100),
    ]);
    setRegistrations(regs);
    setPendingEdits(edits);
    setAllProfiles(profiles);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleApprove = async (registration: BusinessRegistration) => {
    setBusyId(registration.id);
    const res = await BusinessService.approveRegistration(registration.id, adminId);
    setBusyId(null);
    if (!res.success) { toast.error(res.error || 'Could not approve.'); return; }
    toast.success('Approved. They can pay whenever they are ready.');
    await load();
  };

  const handleDeny = async (registration: BusinessRegistration) => {
    const reason = window.prompt(
      `Why is this application declined? The applicant sees this, and cannot reapply for ${BUSINESS_COOLDOWN_DAYS} days.`,
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    setBusyId(registration.id);
    const res = await BusinessService.denyRegistration(registration.id, adminId, reason);
    setBusyId(null);
    if (!res.success) { toast.error(res.error || 'Could not deny.'); return; }
    toast.success('Declined, and the applicant has been told why.');
    await load();
  };

  const handleEdit = async (profile: BusinessProfile, approve: boolean) => {
    let reason: string | undefined;
    if (!approve) {
      const input = window.prompt('Why is this change declined? The owner sees this.');
      if (input === null) return;
      if (!input.trim()) { toast.error('A reason is required.'); return; }
      reason = input;
    }
    setBusyId(profile.id);
    const res = await BusinessService.resolvePendingEdit(profile.id, adminId, approve, reason);
    setBusyId(null);
    if (!res.success) { toast.error(res.error || 'Could not resolve that change.'); return; }
    toast.success(approve ? 'Change applied.' : 'Change declined.');
    await load();
  };

  const handleSuspend = async (profile: BusinessProfile) => {
    const reason = window.prompt(
      `Why is ${profile.businessName} being suspended? The owner sees this. Their personal ` +
      `account and any other businesses they own are not affected.`,
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    setBusyId(profile.id);
    const res = await BusinessService.suspendProfile(profile.id, adminId, reason);
    setBusyId(null);
    if (!res.success) { toast.error(res.error || 'Could not suspend.'); return; }
    toast.success('Suspended. It is now hidden from Explore, search and its public link.');
    await load();
  };

  const handleRestore = async (profile: BusinessProfile) => {
    setBusyId(profile.id);
    const res = await BusinessService.unsuspendProfile(profile.id, adminId);
    setBusyId(null);
    if (!res.success) { toast.error(res.error || 'Could not restore.'); return; }
    toast.success('Restored.');
    await load();
  };

  const cardStyle: React.CSSProperties = {
    background: t.inset, border: `1px solid ${t.border}`,
    borderRadius: 12, padding: 12, marginBottom: 10,
  };
  const actionButton = (bg: string): React.CSSProperties => ({
    flex: 1, height: 36, borderRadius: 9, border: 'none', background: bg,
    color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  });
  const tabButton = (id: QueueTab, label: string, count: number) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      style={{
        flex: 1, height: 34, borderRadius: 8, cursor: 'pointer',
        border: `1px solid ${tab === id ? t.accent : t.border}`,
        background: tab === id ? t.accentSoft : 'transparent',
        color: tab === id ? t.accent : t.textMuted,
        fontSize: 12, fontWeight: 700,
      }}
    >
      {label}{count > 0 ? ` (${count})` : ''}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {tabButton('applications', 'Applications', registrations.length)}
        {tabButton('edits', 'Changes', pendingEdits.length)}
        {tabButton('live', 'Profiles', 0)}
      </div>

      {loading ? (
        <div style={{ padding: 28, display: 'flex', justifyContent: 'center' }}>
          <Loader2 style={{ width: 22, height: 22, color: t.accent }} className="animate-spin" />
        </div>
      ) : (
        <>
          {tab === 'applications' && (
            registrations.length === 0 ? (
              <Empty t={t} text="No applications waiting." />
            ) : registrations.map(registration => (
              <div key={registration.id} style={cardStyle}>
                {/* The business leads, the applicant follows. A moderator is
                    deciding about a business, and the account behind it is
                    context rather than the headline. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <Briefcase style={{ width: 15, height: 15, color: t.accent, flexShrink: 0 }} />
                  <span
                    style={{
                      flex: 1, fontSize: 14, fontWeight: 700, color: t.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {registration.businessName || 'Unnamed business'}
                  </span>
                  <Pill t={t} status="pending" />
                </div>
                <p style={{ margin: '0 0 8px 23px', fontSize: 11, color: t.textMuted }}>
                  {registration.ownerName || 'Unknown'}
                  {registration.ownerUsername ? ` @${String(registration.ownerUsername).replace(/^@/, '')}` : ''}
                </p>

                <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.5, color: t.text, whiteSpace: 'pre-wrap' }}>
                  {registration.pitchText}
                </p>

                {registration.customerBase && registration.customerBase.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                    {registration.customerBase.map(keyword => (
                      <span
                        key={keyword}
                        style={{
                          background: t.accentSoft, color: t.accent,
                          fontSize: 10, fontWeight: 600,
                          padding: '3px 8px', borderRadius: 999,
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}

                {/* Both links are the vetting material: the shop is what they
                    are selling, the social is whether anybody believes them. */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, marginBottom: 10 }}>
                  <ReviewLink t={t} label="Shop" url={registration.shopLink} />
                  {registration.socialLink && (
                    <ReviewLink t={t} label="Social" url={registration.socialLink} />
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => handleDeny(registration)}
                    disabled={busyId === registration.id}
                    style={{ ...actionButton(t.danger), opacity: busyId === registration.id ? 0.6 : 1 }}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApprove(registration)}
                    disabled={busyId === registration.id}
                    style={{ ...actionButton(t.success), opacity: busyId === registration.id ? 0.6 : 1 }}
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))
          )}

          {tab === 'edits' && (
            pendingEdits.length === 0 ? (
              <Empty t={t} text="No name or link changes waiting." />
            ) : pendingEdits.map(profile => (
              <div key={profile.id} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Pencil style={{ width: 15, height: 15, color: t.warning, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: t.text }}>
                    {profile.businessName}
                  </span>
                </div>
                {profile.pendingEdit?.businessName && (
                  <Change t={t} label="Name" from={profile.businessName} to={profile.pendingEdit.businessName} />
                )}
                {profile.pendingEdit?.shopLink && (
                  <Change t={t} label="Shop link" from={profile.shopLink} to={profile.pendingEdit.shopLink} />
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => handleEdit(profile, false)}
                    disabled={busyId === profile.id}
                    style={{ ...actionButton(t.danger), opacity: busyId === profile.id ? 0.6 : 1 }}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEdit(profile, true)}
                    disabled={busyId === profile.id}
                    style={{ ...actionButton(t.success), opacity: busyId === profile.id ? 0.6 : 1 }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            ))
          )}

          {tab === 'live' && (
            allProfiles.length === 0 ? (
              <Empty t={t} text="No business profiles yet." />
            ) : allProfiles.map(profile => (
              <div key={profile.id} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span
                    style={{
                      flex: 1, fontSize: 13, fontWeight: 700, color: t.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {profile.businessName}
                  </span>
                  <Pill t={t} status={profile.status} />
                </div>
                <p style={{ margin: '0 0 10px', fontSize: 11, color: t.textMuted }}>
                  {profile.viewCount || 0} views · {profile.storeTapCount || 0} store taps ·{' '}
                  {profile.confirmedPurchaseCount || 0} confirmed purchases
                </p>
                {profile.status === 'active' ? (
                  <button
                    type="button"
                    onClick={() => handleSuspend(profile)}
                    disabled={busyId === profile.id}
                    style={{
                      ...actionButton(t.danger), width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      opacity: busyId === profile.id ? 0.6 : 1,
                    }}
                  >
                    <ShieldAlert style={{ width: 14, height: 14 }} />
                    Suspend this profile
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleRestore(profile)}
                    disabled={busyId === profile.id}
                    style={{
                      ...actionButton(t.success), width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      opacity: busyId === profile.id ? 0.6 : 1,
                    }}
                  >
                    <RotateCcw style={{ width: 14, height: 14 }} />
                    Restore
                  </button>
                )}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}

function ReviewLink({ t, label, url }: { t: any; label: string; url: string }) {
  return (
    <button
      type="button"
      onClick={() => openExternalUrl(url)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: t.accent, fontSize: 12, textAlign: 'left',
      }}
    >
      <span style={{ color: t.textMuted, flexShrink: 0 }}>{label}</span>
      <span
        style={{
          textDecoration: 'underline',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {url}
      </span>
      <ExternalLink style={{ width: 12, height: 12, flexShrink: 0 }} />
    </button>
  );
}

function Empty({ t, text }: { t: any; text: string }) {
  return (
    <p style={{ padding: 24, margin: 0, textAlign: 'center', fontSize: 13, color: t.textMuted }}>
      {text}
    </p>
  );
}

function Pill({ t, status }: { t: any; status: string }) {
  const pill = statusPill(t, status);
  return (
    <span
      style={{
        background: pill.bg, color: pill.fg, fontSize: 10, fontWeight: 700,
        padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
      }}
    >
      {pill.label}
    </span>
  );
}

function Change({ t, label, from, to }: { t: any; label: string; from: string; to: string }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: t.textMuted }}>{label}</p>
      <p style={{ margin: '2px 0 0', fontSize: 12, color: t.textMuted, textDecoration: 'line-through', wordBreak: 'break-all' }}>
        {from}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: 12, color: t.text, wordBreak: 'break-all' }}>{to}</p>
    </div>
  );
}
